import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bell, Menu, Moon, Search, Sun } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { initialsOf } from "@/lib/utils";
import { ROLE_LABELS } from "@/config/constants";

export function Topbar({ onOpenMobileMenu, onOpenSearch }) {
    const { user, logout } = useAuth();
    const { theme, toggle } = useTheme();
    const navigate = useNavigate();

    const isMac = typeof navigator !== "undefined" && /Mac|iPad|iPhone|iPod/.test(navigator.platform);
    const shortcut = isMac ? "⌘K" : "Ctrl K";

    return (
        <header className="sticky top-0 z-20 flex h-14 items-center justify-between gap-3 border-b bg-background/95 px-4 backdrop-blur supports-[backdrop-filter]:bg-background/70 sm:px-6">
            <div className="flex items-center gap-2">
                <Button
                    variant="ghost"
                    size="icon"
                    className="md:hidden"
                    onClick={onOpenMobileMenu}
                    aria-label="Abrir menu"
                    data-testid="topbar-mobile-menu"
                >
                    <Menu className="h-5 w-5" />
                </Button>
            </div>

            <button
                type="button"
                onClick={onOpenSearch}
                className="group hidden h-9 flex-1 max-w-md items-center gap-2 rounded-lg border bg-muted/60 px-3 text-sm text-muted-foreground hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring md:flex"
                data-testid="topbar-global-search-trigger"
            >
                <Search className="h-4 w-4" />
                <span className="flex-1 text-left">Buscar clientes, ativos, técnicos…</span>
                <kbd className="pointer-events-none rounded border bg-background px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                    {shortcut}
                </kbd>
            </button>

            <div className="flex items-center gap-1">
                <Button
                    variant="ghost"
                    size="icon"
                    className="md:hidden"
                    onClick={onOpenSearch}
                    aria-label="Buscar"
                    data-testid="topbar-mobile-search"
                >
                    <Search className="h-5 w-5" />
                </Button>

                <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Notificações"
                    data-testid="topbar-notifications"
                    title="Sem novas notificações"
                >
                    <Bell className="h-5 w-5" />
                </Button>

                <Button
                    variant="ghost"
                    size="icon"
                    onClick={toggle}
                    aria-label="Alternar tema"
                    data-testid="topbar-theme-toggle"
                >
                    {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
                </Button>

                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <button
                            type="button"
                            className="flex items-center gap-2 rounded-lg px-1.5 py-1 hover:bg-accent/60"
                            data-testid="topbar-user-menu"
                            aria-label="Menu do usuário"
                        >
                            <Avatar className="h-8 w-8">
                                <AvatarFallback className="bg-secondary text-secondary-foreground text-xs">
                                    {initialsOf(user?.name)}
                                </AvatarFallback>
                            </Avatar>
                        </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                        <DropdownMenuLabel className="flex flex-col">
                            <span className="text-sm font-medium">{user?.name}</span>
                            <span className="text-xs font-normal text-muted-foreground">
                                {user?.email}
                            </span>
                            <span className="mt-1 text-[10px] uppercase tracking-wider text-muted-foreground">
                                {ROLE_LABELS[user?.role] || user?.role}
                            </span>
                        </DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem asChild data-testid="user-menu-profile">
                            <Link to="/profile">Meu Perfil</Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild data-testid="user-menu-settings">
                            <Link to="/settings">Configurações</Link>
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                            onClick={() => {
                                logout();
                                navigate("/login", { replace: true });
                            }}
                            data-testid="user-menu-logout"
                        >
                            Sair
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
        </header>
    );
}
